import React, { useCallback, useState } from "react";
import { MicrosoftLogo } from "../../ui-library/_icons/MicrosoftLogo.tsx";
import { Button } from "../../ui-library/Button/Button.tsx";
import { Input } from "../../ui-library/Input/Input.tsx";
import { LoadingDots } from "../../ui-library/LoadingDots/LoadingDots.tsx";
import styles from "./Login.module.css";

const isIpsosEmail = (email: string): boolean => {
  return email.toLowerCase().endsWith("@ipsos.com");
};

export const ConditionalLoginForm: React.FC<{
  onPasswordlessSubmit: (email: string) => Promise<void>;
  onMicrosoftLogin?: () => Promise<void>;
  disabled: boolean;
  failedEmail?: string | null;
}> = ({ onPasswordlessSubmit, onMicrosoftLogin, disabled, failedEmail }) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");

  const handleConditionalSubmit = useCallback(
    async (email: string): Promise<void> => {
      setLoading(true);
      try {
        if (isIpsosEmail(email) && onMicrosoftLogin) {
          await onMicrosoftLogin();
        } else {
          await onPasswordlessSubmit(email);
        }
      } finally {
        setLoading(false);
      }
    },
    [onPasswordlessSubmit, onMicrosoftLogin],
  );

  return (
    <>
      <div className={styles.PasswordlessContainer}>
        <div className={styles.PasswordlessHeader}>
          <h1 className={styles.PasswordlessTitle}>Sign in with email link</h1>
          <p className={styles.PasswordlessSubtitle}>
            Enter your email address to receive a sign-in link.
          </p>
        </div>
        <form
          className={styles.PasswordlessForm}
          onSubmit={(e) => {
            e.preventDefault();
            handleConditionalSubmit(email.trim());
          }}
        >
          <Input
            className={styles.PasswordlessInput}
            placeholder="Enter your email"
            inputMode="email"
            autoComplete="email"
            id={"email"}
            name={"email"}
            type={"email"}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button
            className={styles.PasswordlessButton}
            type="submit"
            disabled={disabled || loading || !email.trim()}
          >
            Send sign-in link
          </Button>
          {failedEmail && (
            <p className={styles.ErrorMessage}>
              Failed to send link to {failedEmail}. Please try again.
            </p>
          )}
        </form>
      </div>

      {onMicrosoftLogin && (
        <>
          <div className={styles.Divider}>
            <span className={styles.DividerText}>
              Or use your Ipsos credentials to log in
            </span>
          </div>
          <Button
            className={styles.MicrosoftButton}
            onClick={async () => {
              await onMicrosoftLogin();
            }}
            disabled={disabled || loading}
          >
            <MicrosoftLogo size={16} className={styles.MicrosoftLogo} />
            Sign in with Microsoft {(disabled || loading) && <LoadingDots />}
          </Button>
        </>
      )}
    </>
  );
};
